import { css, styled } from 'styled-components';

import { ButtonGroupProps, ButtonProps } from '@src/components/atoms/inputs/button/types';
import { CssRecord } from '@src/types/utils/styles';

type ButtonComponentProps = {
  $variant: Required<ButtonProps>['variant'];
  $isLoading: Required<ButtonProps>['isLoading'];
  $size: Required<ButtonProps>['size'];
};

const borderVariant = css`
  box-shadow: inset 0 0 0 1px currentColor;
`;

const variantStyles: CssRecord<ButtonComponentProps['$variant']> = {
  primary: css<{ $isLoading: boolean }>`
    background-color: var(--color-numa-black--400);

    &:focus,
    &:hover {
      background-color: var(--color-primary);
    }

    &:focus-visible {
      box-shadow: var(--shadow--focus-ring);
    }

    &:disabled {
      background-color: var(--color-numa-black--200);
    }

    ${({ $isLoading }) =>
      $isLoading &&
      css`
        background-color: var(--color-primary);
      `}
  `,
  secondary: css<{ $isLoading: boolean }>`
    color: var(--color-numa-black--400);
    background-color: var(--color-white);
    ${() => borderVariant}

    &:focus,
    &:hover {
      color: var(--color-primary);
    }

    &:focus {
      background-color: var(--color-numa-green--50);
    }

    ${({ $isLoading }) =>
      $isLoading &&
      css`
        background-color: var(--color-numa-green--50);
      `}
  `,
  negative: css`
    color: var(--color-numa-red--100);
    background-color: var(--color-white);
    ${() => borderVariant}

    &:focus,
    &:hover {
      color: var(--color-numa-red--200);
    }

    &:focus {
      background-color: var(--color-numa-red--50);
    }
  `,
};

const sizeStyles: CssRecord<ButtonComponentProps['$size']> = {
  normal: css`
    padding: var(--spacing-6) var(--spacing-8);
  `,
  small: css`
    padding: var(--spacing-4) var(--spacing-8);
  `,
};

const wrapperStyles = css<ButtonComponentProps>`
  display: flex;
  justify-content: center;
  align-items: center;
  position: relative;
  margin-block: 0;
  margin-inline: 0;
  color: var(--color-white);
  border: 1px solid var(--color-numa-black--400);
  cursor: pointer;
  font: var(--font-body--medium);
  transition: var(--transition--default);
  transition-property: background-color, color;
  border-radius: 8px;

  @media screen and (min-width: 768px) {
    display: inline-flex;
  }

  svg {
    display: block;
  }

  &:focus {
    outline: none;
  }

  &:disabled {
    color: var(--color-numa-black--250);
    pointer-events: none;
  }

  ${({ $isLoading }) =>
    $isLoading &&
    css`
      pointer-events: none;
    `}

  ${({ $size }) => sizeStyles[$size]}
  ${({ $variant }) => variantStyles[$variant]}
`;
export const ButtonWrapper = styled.button<ButtonComponentProps>`
  ${wrapperStyles}
`;
export const LinkButtonWrapper = styled.a<ButtonComponentProps>`
  ${wrapperStyles}
`;

export const ChildContainer = styled.span<{ $isVisible: boolean }>`
  display: flex;
  align-items: center;
  gap: var(--spacing-4);
  visibility: ${({ $isVisible }) => ($isVisible ? 'visible' : 'hidden')};
`;

const Indicator = styled.div`
  display: inline-flex;
  justify-content: center;
  align-items: center;
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
`;

export const LoadingIndicator = styled(Indicator)``;

export const SuccessIndicator = styled(Indicator)`
  gap: var(--spacing-1);
`;

export const ButtonGroupWrapper = styled.div<Pick<ButtonGroupProps, 'alignment'>>`
  display: flex;
  justify-content: ${({ alignment }) => alignment};
`;

export const ButtonsWrapper = styled.div`
  --gap: var(--spacing-4);
  --max-width: 28em;

  display: flex;
  gap: var(--gap);
  flex: 1 1 auto;
  flex-wrap: wrap-reverse;
  max-width: var(--max-width);

  > * {
    flex: 1 1 calc((var(--max-width) - var(--gap)) / 2);
  }
`;
